/**
 * Согласие на аналитические cookie — Consent Mode v2.
 *
 * ПОРЯДОК ВАЖЕН И ОН ЗДЕСЬ ЕДИНСТВЕННЫЙ, КОТОРЫЙ РАБОТАЕТ. `consent default`
 * обязан попасть в `dataLayer` ДО того, как загрузится `gtag.js`, — иначе
 * библиотека успевает поставить `_ga` раньше, чем узнает, что ей нельзя.
 * Поэтому очередь наполняется синхронно (`gtag.ts` → `boot()`), а тег
 * подключается следующей строкой.
 *
 * ЧТО ЗНАЧИТ ОТКАЗ. `analytics_storage: 'denied'` — это не «аналитики нет», это
 * cookieless-режим: GA4 всё равно считает просмотры, но без `_ga`, без
 * идентификатора устройства между визитами и без склейки сессий. Рекламные
 * разрешения (`ad_storage`, `ad_user_data`, `ad_personalization`) не выдаются
 * НИКОГДА и не появляются ни в одном `update` — рекламных функций у этого
 * приложения нет, и обещание в политике конфиденциальности держится кодом, а не
 * настройкой в чужом интерфейсе, которую можно переключить мышкой.
 *
 * «Только необходимое» остаётся cookieless навсегда: выбор запоминается и
 * плашка больше не приходит. Передумать можно на `/privacy` — ссылка
 * «изменить выбор» стирает запись, и плашка возвращается.
 */
import { ACHP_CONSENT_STORAGE_KEY } from './config'

export type ConsentChoice = 'granted' | 'necessary'

/** Что лежит в `localStorage`: решение и когда его приняли. */
interface StoredConsent {
  choice: ConsentChoice
  at: string
}

/**
 * `localStorage` бросает исключение чаще, чем принято думать: приватный режим
 * Safari, отключённые сторонние данные, переполненная квота. Согласие — не то
 * место, где приложение имеет право упасть: не прочиталось — значит, выбора
 * ещё нет, и человека спросят снова.
 */
export function readConsent(): ConsentChoice | null {
  if (typeof localStorage === 'undefined') return null
  try {
    const raw = localStorage.getItem(ACHP_CONSENT_STORAGE_KEY)
    if (!raw) return null
    const parsed: unknown = JSON.parse(raw)
    const choice = (parsed as StoredConsent | null)?.choice
    return choice === 'granted' || choice === 'necessary' ? choice : null
  } catch {
    return null
  }
}

/** Запоминает решение. Не записалось — плашка придёт снова, и это честно. */
export function writeConsent(choice: ConsentChoice): void {
  if (typeof localStorage === 'undefined') return
  try {
    const value: StoredConsent = { choice, at: new Date().toISOString() }
    localStorage.setItem(ACHP_CONSENT_STORAGE_KEY, JSON.stringify(value))
  } catch {
    /* см. комментарий к readConsent */
  }
}

/** «Изменить выбор» на странице политики. */
export function forgetConsent(): void {
  if (typeof localStorage === 'undefined') return
  try {
    localStorage.removeItem(ACHP_CONSENT_STORAGE_KEY)
  } catch {
    /* см. комментарий к readConsent */
  }
}

/**
 * Значения по умолчанию — то, с чем GA4 стартует до любого ответа человека.
 * Всё запрещено; разрешить `analytics_storage` может только явное «Принять».
 */
export const CONSENT_DEFAULTS = {
  analytics_storage: 'denied',
  ad_storage: 'denied',
  ad_user_data: 'denied',
  ad_personalization: 'denied',
} as const

/**
 * Что уходит в `consent update` при согласии. Рекламные ключи ПОВТОРЯЮТСЯ со
 * значением `denied` намеренно: `update` перезаписывает только то, что назвал,
 * и явный отказ рядом с разрешением — это документ, который читается в
 * отладчике GA, а не подразумевается.
 */
export const CONSENT_GRANTED = {
  ...CONSENT_DEFAULTS,
  analytics_storage: 'granted',
} as const
